/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Close, Fullscreen } from '@mui/icons-material'
import { Box, CircularProgress, IconButton, Typography, Modal } from '@mui/joy'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { loadScript, loadStylesheet } from 'utils/script'
import { OrderRouteMapClient } from './api'
import { getKeyPointsAlongRoute } from './utils'

declare global {
    interface Window {
        H: any
    }
}

interface OrderRouteMapProps {
    polyline?: string | null | any
    onWeatherUpdate?: (warnings: Array<{ condition: string; location: string; temp?: number; icon: string }>) => void
}

async function getPolyline(url: string) {
    try {
        const response = await fetch(url)
        const data = await response.text()
        return data
    } catch (error) {
        console.error('Error fetching polyline:', error)
        return null
    }
}

const OrderRouteMap: React.FC<OrderRouteMapProps> = ({ polyline, onWeatherUpdate }) => {
    const mapRef = useRef<HTMLDivElement>(null)
    const modalMapRef = useRef<HTMLDivElement>(null)
    const [mapState, setMapState] = useState({
        isLoaded: false,
        isLoading: true,
        error: null as string | null,
    })
    const [isMapModalOpen, setIsMapModalOpen] = useState(false)
    const [polylineData, setPolylineData] = useState<string | null>(null)

    const fetchPolyline = useCallback(async () => {
        if (!polyline) return

        try {
            setMapState((prev) => ({ ...prev, isLoading: true, error: null }))

            let fetchedPolylineData: string | null = null

            if (typeof polyline === 'string' && polyline.startsWith('https')) {
                try {
                    fetchedPolylineData = await getPolyline(polyline)
                } catch (error) {
                    console.error('DEBUG: OrderRouteMap - Failed to fetch polyline from URL:', error)
                    setMapState((prev) => ({ ...prev, error: 'Failed to fetch route data from storage' }))
                    return
                }
            } else if (typeof polyline === 'object' && polyline !== null) {
                if (polyline.polylineMediaLink) {
                    try {
                        fetchedPolylineData = await getPolyline(polyline.polylineMediaLink)
                    } catch (error) {
                        console.error('DEBUG: OrderRouteMap - Failed to fetch polyline from media link:', error)
                        setMapState((prev) => ({ ...prev, error: 'Failed to fetch route data from storage' }))
                        return
                    }
                }
                // Then check for direct polyline property
                else if (polyline.polyline) {
                    fetchedPolylineData = polyline.polyline
                }
                // If neither exists, check if the object itself is a polyline string
                else if (typeof polyline.toString === 'function' && polyline.toString().length > 20) {
                    fetchedPolylineData = polyline.toString()
                } else {
                    setMapState((prev) => ({ ...prev, error: 'Invalid polyline data format' }))
                    return
                }
            }
            // Case 3: Direct polyline string
            else if (typeof polyline === 'string') {
                fetchedPolylineData = polyline
            }
            // Case 4: Unknown format
            else {
                console.error('DEBUG: OrderRouteMap - Unknown polyline format:', polyline)
                setMapState((prev) => ({ ...prev, error: 'Unknown polyline data format' }))
                return
            }

            if (fetchedPolylineData === '') {
                setMapState((prev) => ({ ...prev, error: 'No route data available' }))
                return
            }

            if (!fetchedPolylineData) {
                setMapState((prev) => ({ ...prev, error: 'Invalid route data format' }))
                return
            }

            setPolylineData(fetchedPolylineData)
        } catch (err) {
            console.error('Failed to fetch polyline:', err)
            setMapState((prev) => ({ ...prev, error: 'Failed to load route data' }))
        } finally {
            setMapState((prev) => ({ ...prev, isLoading: false }))
        }
    }, [polyline])

    const loadHereMaps = useCallback(async () => {
        try {
            setMapState((prev) => ({ ...prev, isLoading: true, error: null }))

            // Load core script first
            await loadScript('https://js.api.here.com/v3/3.1/mapsjs-core.js')

            // Wait for H to be defined with timeout
            await new Promise<void>((resolve, reject) => {
                const timeout = setTimeout(() => {
                    reject(new Error('HERE Maps initialization timeout'))
                }, 10000)

                const checkH = () => {
                    if (window.H) {
                        clearTimeout(timeout)
                        resolve()
                    } else {
                        setTimeout(checkH, 100)
                    }
                }
                checkH()
            })

            // Load remaining scripts in sequence
            await loadScript('https://js.api.here.com/v3/3.1/mapsjs-service.js')
            await loadScript('https://js.api.here.com/v3/3.1/mapsjs-mapevents.js')
            await loadScript('https://js.api.here.com/v3/3.1/mapsjs-ui.js')
            await loadStylesheet('https://js.api.here.com/v3/3.1/mapsjs-ui.css')

            setMapState((prev) => ({ ...prev, isLoaded: true }))
        } catch (error) {
            console.error('Error loading HERE Maps:', error)
            setMapState((prev) => ({ ...prev, error: 'Failed to load map components' }))
        } finally {
            setMapState((prev) => ({ ...prev, isLoading: false }))
        }
    }, [])

    // New function to get weather along the route with a single API call
    const getWeatherAlongRoute = useCallback(async (polylineData: string) => {
        try {
            if (!window.H || !polylineData) return

            // Get the route coordinates
            const routeCoordinates = window.H.util.flexiblePolyline.decode(polylineData).polyline

            // Get key points along the route: origin, destination, and a few points in between
            const keyPoints = getKeyPointsAlongRoute(routeCoordinates)

            // Create a batch request for weather data
            const weatherPromises = keyPoints.map(([lat, lng]) => OrderRouteMapClient.getWeatherByCoordinates(lat, lng))

            // Wait for all weather requests to complete
            const weatherResults = await Promise.all(weatherPromises)

            // Process the weather data to find warnings
            const warnings: Array<{ condition: string; location: string; temp?: number; icon: string }> = []

            weatherResults.forEach((weatherData) => {
                const temp = weatherData.main.temp
                const weather = weatherData.weather[0]
                const location = weatherData.name

                if (temp < 1) {
                    warnings.push({ condition: 'freezing', location, temp, icon: weather.icon })
                }
                if (weather.main.toLowerCase().includes('rain')) {
                    warnings.push({ condition: 'rain', location, icon: weather.icon })
                }
                if (weather.main.toLowerCase().includes('storm') || weather.main.toLowerCase().includes('thunder')) {
                    warnings.push({ condition: 'storm', location, icon: weather.icon })
                }
                if (weather.main.toLowerCase().includes('snow')) {
                    warnings.push({ condition: 'snow', location, icon: weather.icon })
                }
            })

            onWeatherUpdate?.(warnings)
        } catch (error) {
            console.error('Error fetching weather along route:', error)
        }
    }, [])

    const setupMap = useCallback(
        async (containerRef: React.RefObject<HTMLDivElement>) => {
            if (!mapState.isLoaded || !containerRef.current || !window.H || !polylineData) return null

            let map: any = null
            let behavior: any = null
            let ui: any = null

            try {
                setMapState((prev) => ({ ...prev, isLoading: true, error: null }))

                const platform = new window.H.service.Platform({
                    apikey: process.env.REACT_APP_HERE_API_KEY,
                })

                const defaultLayers = platform.createDefaultLayers()

                map = new window.H.Map(containerRef.current, defaultLayers.vector.normal.map, {
                    center: { lat: 40.7128, lng: -74.006 },
                    zoom: 10,
                    pixelRatio: window.devicePixelRatio || 1,
                })

                behavior = new window.H.mapevents.Behavior(new window.H.mapevents.MapEvents(map))
                ui = window.H.ui.UI.createDefault(map, defaultLayers)

                // Use HERE's built-in flexible polyline decoder
                const routeLineString = window.H.geo.LineString.fromFlexiblePolyline(polylineData)
                const routePolyline = new window.H.map.Polyline(routeLineString, {
                    style: { lineWidth: 5, strokeColor: 'blue' },
                })

                map.addObject(routePolyline)

                const bounds = routePolyline.getBoundingBox()
                if (bounds) {
                    map.getViewModel().setLookAtData({
                        bounds,
                        animation: 0,
                    })
                }

                // Get weather along the route with a single API call
                await getWeatherAlongRoute(polylineData)

                // Handle resize
                const handleResize = () => {
                    map?.getViewPort()?.resize()
                }

                const resizeObserver = new ResizeObserver(handleResize)
                if (containerRef.current) {
                    resizeObserver.observe(containerRef.current)
                }

                // Return cleanup function
                return () => {
                    resizeObserver.disconnect()
                    behavior?.disable()
                    ui?.dispose()
                    map?.dispose()
                }
            } catch (error) {
                console.error('Error initializing map:', error)
                setMapState((prev) => ({ ...prev, error: 'Failed to initialize map' }))
            } finally {
                setMapState((prev) => ({ ...prev, isLoading: false }))
            }
        },
        [mapState.isLoaded, polylineData, getWeatherAlongRoute]
    )

    const openMapModal = () => {
        setIsMapModalOpen(true)
        // Reset any previous map instance
        if (modalMapRef.current) {
            modalMapRef.current.innerHTML = ''
        }
    }

    const closeMapModal = () => {
        setIsMapModalOpen(false)
    }

    // Initial setup
    useEffect(() => {
        if (!window.H) {
            loadHereMaps()
        } else {
            setMapState((prev) => ({ ...prev, isLoaded: true, isLoading: false }))
        }

        return () => {
            const scripts = document.querySelectorAll('script[src*="js.api.here.com"]')
            const styles = document.querySelectorAll('link[href*="js.api.here.com"]')
            scripts.forEach((script) => script.remove())
            styles.forEach((style) => style.remove())
        }
    }, [loadHereMaps])

    // Fetch polyline when prop changes
    useEffect(() => {
        if (polyline) {
            fetchPolyline()
        }
    }, [polyline, fetchPolyline])

    // Setup main map when all dependencies are ready
    useEffect(() => {
        if (mapState.isLoaded && polyline && polylineData) {
            setupMap(mapRef)
        }
    }, [mapState.isLoaded, polyline, polylineData, setupMap])

    // Setup modal map when modal is opened
    useEffect(() => {
        let modalMapCleanup: (() => void) | null = null
        let isActive = true

        if (isMapModalOpen && mapState.isLoaded && polyline && polylineData) {
            // Small delay to ensure the modal DOM is ready
            const timer = setTimeout(async () => {
                if (!isActive) return

                if (modalMapRef.current) {
                    try {
                        // Since setupMap is async, we need to await it
                        const cleanup = await setupMap(modalMapRef)
                        if (cleanup && isActive) {
                            modalMapCleanup = cleanup
                        }
                    } catch (error) {
                        console.error('Error setting up modal map:', error)
                    }
                }
            }, 100)

            return () => {
                isActive = false
                clearTimeout(timer)
                if (modalMapCleanup) modalMapCleanup()
            }
        }
    }, [isMapModalOpen, mapState.isLoaded, polyline, polylineData, setupMap])

    return (
        <Box>
            <div
                style={{
                    position: 'relative',
                    width: '100%',
                    height: '200px',
                    backgroundColor: '#F4F7F9',
                }}
            >
                {mapState.isLoading && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 1,
                        }}
                    >
                        <CircularProgress size="sm" />
                        <Typography level="body-sm">Loading map...</Typography>
                    </Box>
                )}

                {mapState.error && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            textAlign: 'center',
                        }}
                    >
                        <Typography level="body-sm" color="danger">
                            {mapState.error}
                        </Typography>
                    </Box>
                )}

                <div ref={mapRef} style={{ width: '100%', height: '100%' }} />

                <IconButton
                    onClick={openMapModal}
                    sx={{
                        position: 'absolute',
                        bottom: '10px',
                        right: '10px',
                        backgroundColor: 'white',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                        '&:hover': {
                            backgroundColor: '#f5f5f5',
                        },
                    }}
                >
                    <Fullscreen />
                </IconButton>

                <Modal
                    open={isMapModalOpen}
                    onClose={closeMapModal}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                    slotProps={{
                        backdrop: {
                            sx: { backdropFilter: 'blur(3px)' },
                        },
                    }}
                >
                    <Box
                        sx={{
                            position: 'relative',
                            width: '90vw',
                            height: '80vh',
                            bgcolor: 'background.paper',
                            borderRadius: 'md',
                            boxShadow: 'lg',
                            p: 0,
                            overflow: 'hidden',
                        }}
                    >
                        <IconButton
                            onClick={closeMapModal}
                            sx={{
                                position: 'absolute',
                                top: '10px',
                                right: '10px',
                                zIndex: 10,
                                bgcolor: 'background.surface',
                                boxShadow: 'sm',
                            }}
                        >
                            <Close />
                        </IconButton>

                        <div ref={modalMapRef} style={{ width: '100%', height: '100%', backgroundColor: '#F4F7F9' }} />

                        {/* Loading indicator for modal map */}
                        {isMapModalOpen && mapState.isLoading && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    zIndex: 11,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 1,
                                }}
                            >
                                <CircularProgress size="md" />
                                <Typography level="body-md">Loading map...</Typography>
                            </Box>
                        )}
                    </Box>
                </Modal>
            </div>
        </Box>
    )
}

export default OrderRouteMap
